import React, { useState } from 'react';
import { Modal, Button, Upload, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import FormInput from '../inputWrapper/formInput';
import FilterCheckboxes from '../../components/inputWrapper/filterBox';
import CustomButton from '../../components/button';
import TagInput from '../inputWrapper/tagInput';

import {
  setVideoUploading,
  setAudioUploading,
  setVideoUploadError,
  setAudioUploadError,
  resetVideoUploadState,
  resetAudioUploadState,
  resetDocumentUploadState
} from '../../redux/slices/modalSlice';
import {
  handleDocumentUpload,
  handleFileChange,
  handleSubmit,
  handleUpload,
  handleVisibilityChange,
  resetFileList,
  uploadCancelTokens
} from '../../helpers/audioUploadHelper';
import { maxLength, requiredField, validateCheckbox } from '../../helpers/validationHelper';
import { useAuth } from '../../routes/authContext';

import { INPUT_TYPES } from '../../constants/componentTypes';

import { BRImages } from '../../assets/imgs';
import '../../assets/scss/index.scss';
import {
  useDeleteAudioByIdMutation,
  useDeleteVideoByIdMutation
} from '../../redux/slices/mediaApi';

const { Dragger } = Upload;

const UploadAudioModal = ({ visible, onClose, loading, mediaType }) => {
  const [form] = Form.useForm();
  const { openNotification } = useAuth();
  const dispatch = useDispatch();
  const isVideo = mediaType === 'video';
  const [fileList, setFileList] = useState([]);
  const [disabledItems, setDisabledItems] = React.useState([]);
  const {
    isVideoUploading,
    isAudioUploading,
    isVideoUploadComplete,
    isAudioUploadComplete,
    mediaTypes,
    mediaVisibilityOptions
  } = useSelector((state) => state.modal);
  const [deleteVideoById] = useDeleteVideoByIdMutation();
  const [deleteAudioById] = useDeleteAudioByIdMutation();
  const isUploading = isVideo ? isVideoUploading : isAudioUploading;
  const isUploadComplete = isVideo ? isVideoUploadComplete : isAudioUploadComplete;
  const [videoMediaId, setVideoMediaId] = useState(null);
  const [audioMediaId, setAudioMediaId] = useState(null);
  const [audioDuration, setAudioDuration] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [documentIds, setDocumentIds] = useState([]);
  const [tags, setTags] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [audioLink, setAudioLink] = useState(null);
  const Dragprops = {
    name: 'file',
    multiple: false,
    customRequest: (options) =>
      handleUpload({
        ...options,
        isVideo,
        dispatch,
        setMediaId: isVideo ? setVideoMediaId : setAudioMediaId,
        setDuration: isVideo ? setVideoDuration : setAudioDuration,
        openNotification,
        setAudioLink
      }),
    beforeUpload: (file) => {
      if (fileList.length >= 1) {
        openNotification({
          message: `You can only upload one ${isVideo ? 'video' : 'audio'} at a time!`,
          type: 'warning',
          title: 'Warning'
        });
        return Upload.LIST_IGNORE;
      }
      const isValidType = isVideo ? file.type === 'video/mp4' : file.type === 'audio/mpeg';
      if (!isValidType) {
        openNotification({
          message: `You can only upload ${isVideo ? 'MP4' : 'MP3'} files!`,
          type: 'warning',
          title: 'Warning'
        });
        return Upload.LIST_IGNORE;
      }
      if (isVideo) {
        dispatch(setVideoUploading(true));
        dispatch(setVideoUploadError(false));
      } else {
        dispatch(setAudioUploading(true));
        dispatch(setAudioUploadError(false));
      }
      return true;
    },
    onChange: (info) => handleFileChange(info, setFileList),
    onDrop(e) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const invalidFiles = droppedFiles.filter((file) =>
        isVideo ? file.type !== 'video/mp4' : file.type !== 'audio/mpeg'
      );
      if (invalidFiles.length > 0) {
        openNotification({
          message: `Invalid file type detected in dropped files! Only ${isVideo ? 'MP4' : 'MP3'
            } files are allowed.`,
          type: 'warning',
          title: 'Warning'
        });
      }
    },
    onRemove: async (file) => {
      // Handle removal during upload
      if (uploadCancelTokens[file.uid]) {
        uploadCancelTokens[file.uid].cancel();
        delete uploadCancelTokens[file.uid];
        resetFileList(setFileList, isVideo, dispatch);
        return true;
      }

      // Handle removal after upload is complete
      if (isUploadComplete) {
        try {
          if (isVideo && videoMediaId) {
            await deleteVideoById(videoMediaId).unwrap();
          } else if (!isVideo && audioMediaId) {
            await deleteAudioById(audioMediaId).unwrap();
          }
          openNotification({
            message: `${isVideo ? 'Video' : 'Audio'} deleted successfully.`,
            type: 'success',
            title: 'Success'
          });
        } catch (error) {
          openNotification({
            message: `${isVideo ? 'Video' : 'Audio'} deletion failed.`,
            type: 'error',
            title: 'Error'
          });
        }
      }

      resetFileList(setFileList, isVideo, dispatch);
      return true;
    },
    listType: 'picture',
    accept: isVideo ? '.mp4' : '.mp3',

    iconRender: (file, { status }) => {
      return (
        <img
          src={
            isVideo
              ? BRImages.MediaPlayerImages.UploadListVideo
              : BRImages.MediaPlayerImages.UploadListAudio
          }
          alt={`${isVideo ? 'video' : 'audio'}-upload-icon`}
        />
      );
    },
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
      removeIcon: (
        <img
          src={BRImages.CommonImages.CloseCircleIcon}
          alt='close-icon'
          onClick={(e) => {
            e.preventDefault();
          }}
        />
      )
    }
  };

  const DocumentDragProps = {
    name: 'file',
    multiple: true,
    customRequest: (options) =>
      handleDocumentUpload({ ...options, setDocumentIds, openNotification, dispatch }),
    beforeUpload: (file) => {
      const validTypes = ['.pdf', '.doc', '.docx', '.ppt', '.pptx'];
      const isValidType = validTypes.some((type) => file.name.endsWith(type));
      if (!isValidType) {
        openNotification({
          message: 'You can only upload PDF, DOC, DOCX, PPT, or PPTX files!',
          type: 'warning',
          title: 'Warning'
        });

        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onRemove: (file) => {
      openNotification({
        message: `${file.name} removed`,
        type: 'info',
        title: 'Information'
      });
    },
    // listType: "picture",
    accept: '.pdf,.doc,.docx,.ppt,.pptx',
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
      removeIcon: (
        <img
          src={BRImages.CommonImages.CloseCircleIcon}
          alt='close-icon'
          onClick={(e) => {
            e.preventDefault();
          }}
        />
      )
    }
  };
  const resetModalState = () => {
    // Reset all states and form
    form.resetFields();
    setFileList([]);
    setSubmitLoading(false);
    setDisabledItems([]);

    // Reset Redux states
    if (isVideo) {
      setVideoMediaId(null);
      dispatch(resetVideoUploadState());
      setVideoDuration(null);
      setDocumentIds([]);
      setTags([]);
      dispatch(resetDocumentUploadState());
    } else {
      setAudioMediaId(null);
      dispatch(resetAudioUploadState());
      setAudioDuration(null);
      setAudioLink(null);
    }
  };
  const handleCancel = async () => {
    // 1. If upload is in progress: cancel upload and reset modal
    if (isUploading) {
      // Cancel the upload if in progress
      if (fileList.length > 0) {
        const [file] = fileList;
        if (file && uploadCancelTokens[file.uid]) {
          uploadCancelTokens[file.uid].cancel();
          delete uploadCancelTokens[file.uid];
        }
      }

      resetModalState();
      onClose();
      return;
    }

    // 2. If upload is completed: delete the media and reset modal
    if (isUploadComplete) {
      try {
        if (isVideo && videoMediaId) {
          await deleteVideoById(videoMediaId).unwrap();
        }
        if (!isVideo && audioMediaId) {
          await deleteAudioById(audioMediaId).unwrap();
        }
      } catch (error) {
        // If deletion fails, just proceed to close the modal but you can add a notification if needed.
        openNotification({
          message: `${isVideo ? 'Video' : 'Audio'} deletion failed`,
          type: 'error',
          title: 'Error'
        });
      }

      resetModalState();
      onClose();
      return;
    }

    // 3. If neither in progress nor completed: just reset modal state
    resetModalState();
    onClose();
  };

  const handleMediaTypeChange = (selectedValues) => {
    form.setFieldsValue({ mediaType: selectedValues });
    handleVisibilityChange(
      form.getFieldValue('visibility') || [],
      setDisabledItems,
      form,
      selectedValues || []
    );
  };
  return (
    <Modal
      open={visible}
      title={`Upload ${isVideo ? 'Video' : 'Audio'}`}
      onCancel={onClose}
      destroyOnClose={false}
      maskClosable={false}
      footer={
        <div className='flex justify-end assign-modal-footer gap-8'>
          <CustomButton
            title='Cancel'
            variant='outline'
            className='radius-5'
            onClick={handleCancel}
            disabled={isUploading || submitLoading}
          />
          <CustomButton
            title='Save'
            className='radius-5'
            onClick={() =>
              handleSubmit(
                form,
                isVideo ? videoMediaId : audioMediaId,
                documentIds,
                isVideo,
                isVideo ? videoDuration : audioDuration,
                onClose,
                resetModalState,
                setSubmitLoading,
                openNotification,
                tags,
                setFileList,
                audioLink,
                dispatch
              )
            }
            loading={loading || submitLoading}
            disabled={!isUploadComplete || loading || isUploading || submitLoading}
          />
        </div>
      }
      closeIcon={<img src={BRImages.CommonImages.CloseGrayIcon} alt='close-icon' />}
      className={`upload-video-modal common-modal`}
    >
      <Form form={form} layout='vertical' requiredMark={false} className='upload-form-container'>
        {/* Upload Media */}
        <Form.Item
          name='mediaFile'
          valuePropName='fileList'
          getValueFromEvent={(e) => e && e.fileList}
          rules={[{ required: true, message: `Please upload a ${mediaType} file` }]}
          className='mb-16'
        >
          <Dragger {...Dragprops} className='upload-modal-dragger'>
            <img
              src={BRImages.MediaPlayerImages.UploadBtnIcon}
              alt='upload-icon'
              className='mb-8'
            />
            <p className='ant-upload-text m-0 mb-5 manrope-16-bold'>
              Drag & drop {mediaType} or <span className='browse-click cursor-pointer'>Browse</span>
            </p>
            <p className='ant-upload-hint m-0 manrope-14-regular'>
              Supported formats: {isVideo ? 'Mp4' : 'Mp3'}
            </p>
          </Dragger>
        </Form.Item>

        {/* Form Inputs */}
        <FormInput
          label='Title'
          name='title'
          placeholder='Enter title'
          rules={!isUploading && isUploadComplete ? [requiredField('')] : []}
          disabled={isUploading || !isUploadComplete}
          maxLength={50}
        />
        <FormInput
          label='Speaker'
          name='speaker'
          placeholder='Enter speaker name'
          rules={!isUploading && isUploadComplete ? [requiredField('')] : []}
          disabled={isUploading || !isUploadComplete}
          maxLength={50}
        />
        <FormInput
          label='Theme'
          name='theme'
          placeholder='Enter theme'
          rules={!isUploading && isUploadComplete ? [] : []}
          disabled={isUploading || !isUploadComplete}
          maxLength={50}
        />
        {isVideo && (
          <FormInput
            label='Description'
            name='description'
            rules={
              !isUploading && isUploadComplete
                ? [requiredField(''), maxLength(300, 'Maximum length is 300 characters')]
                : []
            }
            type={INPUT_TYPES.TEXT_AREA}
            className='webcast-signin-input radius-10'
            placeholder='Type Description...'
            disabled={isUploading || !isUploadComplete}
            onChange={(e) => {
              form.setFieldsValue({ reason: e.target.value.trimStart() });
            }}
          />
        )}
        {isVideo && (
          <Form.Item
            name='tags'
            rules={
              !isUploading && isUploadComplete ? [requiredField('Please add at least one tag')] : []
            }
            label='Tags'
            valuePropName='value'
            getValueFromEvent={(e) => e || []}
          >
            <TagInput
              value={tags}
              onChange={(newTags) => setTags(newTags)}
              disabled={isUploading || !isUploadComplete}
            />
          </Form.Item>
        )}
        {/* Media Type Filters */}
        <Form.Item
          name='mediaType'
          label={mediaType === 'video' ? 'Video Type' : 'Audio Type'}
          rules={
            !isUploading && isUploadComplete
              ? [validateCheckbox('Please select at least one option')]
              : []
          }
        >
          <FilterCheckboxes
            filters={mediaTypes}
            disabled={isUploading || !isUploadComplete}
            onChange={handleMediaTypeChange}
          />
        </Form.Item>
        <Form.Item
          name='visibility'
          label='Visibility'
          rules={
            !isUploading && isUploadComplete
              ? [validateCheckbox('Please select at least one option')]
              : []
          }
          className='visibility-filter'
        >
          <FilterCheckboxes
            filters={mediaVisibilityOptions}
            value={form.getFieldValue('visibility')}
            onChange={(selectedValues) =>
              handleVisibilityChange(
                selectedValues,
                setDisabledItems,
                form,
                form.getFieldValue('mediaType') || []
              )
            }
            disabledItems={disabledItems}
            disabled={isUploading || !isUploadComplete}
          />
        </Form.Item>

        {/* Document Upload Section */}
        {isVideo && (
          <Form.Item
            name='documents'
            label='Upload Files'
            valuePropName='fileList'
            getValueFromEvent={(e) => e && e.fileList}
          >
            <Upload
              {...DocumentDragProps}
              beforeUpload={() => true}
              className='upload-doc-btn flex'
              disabled={isUploading || !isUploadComplete}
            >
              <Button
                icon={<img src={BRImages.CommonImages.AddBlueIconCircled} alt='upload-icon' />}
                className='btn-primary-outline button-common radius-4 manrope-14-regular w-100 mb-16'
              >
                Upload
              </Button>
            </Upload>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default UploadAudioModal;
